import StaffService from '../../../api/staff-service'
import { getLocal, setLocals } from '../../../utils/local'
import { useNavigate } from 'react-router-dom'
import usePermission from '../../../hooks/usePermission'
import { showToast } from '../../../utils/toast'
import { useState } from 'react'
import Modal from '../../Modal'
import EstateService from '../../../api/estate-service'
import { EstateDto } from '../../../interfaces/estate'
import ModalClose from '../../Modal/ModalClose'
import DefaultButton from '../../Button/Button'
import { EstateStaffProfileDTO } from '../../../interfaces/staff'

interface EstateCardProps {
  id: string
  name?: string
  state?: string
  isOwner?: boolean
  isVerified?: boolean
  imageUrl?: string
  isActive?: boolean
  accepted?: boolean
  handleGetStaffEstates?: () => void
}

const EstateCard = ({
  id,
  name,
  state,
  isVerified,
  isOwner,
  isActive,
  accepted,
  handleGetStaffEstates,
}: EstateCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [estateDetails, setEstateDetails] = useState<EstateDto | null>(null)
  const [acceptInvite, setAcceptInvite] = useState<EstateStaffProfileDTO | null>(null)
  const navigate = useNavigate()
  const { getDefaultRoute } = usePermission()

  const handleGetAccess = (id: string) => {
    StaffService.getStaffEstateAccess(id)
      .then((data) => {
        setLocals({
          token: data.tokens.access.token,
          refreshToken: data.tokens.refresh.token,
          user: data.data.staff,
          estate: data.data.estate,
          permissions: data.data.permissions,
        })
        const route = getDefaultRoute(data.data.permissions)
        navigate(route)
      })
      .catch((err) => {
        showToast({
          id: 'estate-access-error',
          title: 'Access error',
          description: err.message,
          status: 'error',
        })
      })
  }

  const handleStaffInvite = (id: string) => {
    EstateService.acceptEstateInviteStaffResident(id)
      .then((data) => {
        setAcceptInvite(data)
        if (handleGetStaffEstates) {
          handleGetStaffEstates()
        }
      })
      .catch((err) => {
        showToast({
          description: err.message,
          status: 'error',
          id: 'get-estate-error',
          title: 'Error',
        })
      })
  }

  const handleViewEstate = (id: string) => {
    EstateService.getEstateById(id)
      .then((data) => {
        setEstateDetails(data?.data as EstateDto)
        setIsModalOpen(true)
      })
      .catch((err) => {
        showToast({
          description: err.message,
          status: 'error',
          id: 'get-estate-error',
          title: 'Error',
        })
      })
  }
  const handleEditEstate = (id: string) => {
    if (isOwner) {
      navigate(`/estates/${id}/edit`)
    } else {
      showToast({
        description: 'You are not authorized to edit this estate',
        status: 'error',
        id: 'edit-estate-error',
        title: 'Error',
      })
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }
  const localEstate = getLocal('estate');
  const hasAccess = localEstate?.id === id;

  return (
    <div className="w-[317px] p-5 rounded-lg bg-gray-100 shadow-sm">
      <div className="flex flex-col gap-1" onClick={() => handleViewEstate(id)}>
        <p className="text-base font-semibold text-black capitalize cursor-pointer">
          {name}
        </p>
        <p className="text-sm font-medium text-gray-600">{state}</p>
      </div>

      <div className="flex justify-between mt-10">
        <div className="flex flex-col items-start gap-1">
          <span
            className={`text-sm font-normal rounded-md px-2 py-1 ${isVerified === true
              ? 'bg-green-100 text-green-600'
              : isVerified === false
                ? 'bg-red-100 text-red-600'
                : 'bg-yellow-100 text-yellow-600'
              }`}
          >
            {isVerified === true
              ? 'Verified'
              : isVerified === false
                ? 'Unverified'
                : 'Pending'}
          </span>
          {isOwner && (
            <span className="text-sm font-normal text-gray-900 px-2 py-1">Owner</span>
          )}
        </div>
        <div className="flex">

          {
            isVerified && !isActive ? (
              <DefaultButton
                background="gray"
                color="white"
                disabled
                className="w-32 text-center text-base font-medium bg-gray-400 text-white rounded-lg"
              >
                Restricted
              </DefaultButton>
            ) :
              isVerified && hasAccess ? (
                <DefaultButton
                  background="black"
                  color="white"
                  disabled
                  className="w-32 text-center text-base font-medium bg-black text-white rounded-lg"
                >
                  You're here
                </DefaultButton>
              ) : isVerified && isActive && accepted ? (
                <DefaultButton
                  background="black"
                  color="white"
                  onClick={() => handleGetAccess(id)}
                  className="w-32 text-center text-base font-medium bg-black text-white rounded-lg"
                >
                  Get Access
                </DefaultButton>
              ) : (
                isVerified && !accepted && (
                  <DefaultButton
                    background="black"
                    color="white"
                    onClick={() => isOwner ? handleGetAccess(id) : handleStaffInvite(id)}
                    className="w-32 text-center text-base font-medium bg-black text-white rounded-lg"
                  >
                    {isOwner ? 'Get Access' : 'Accept Invite'}
                  </DefaultButton>
                )
              )}
        </div>

      </div>
      <Modal
        open={isModalOpen}
        close={handleCloseModal}
        widthClass="w-[calc(100%-32px)] sm:max-w-md md:max-w-[550px]"
        modalClassName="p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex flex-col">
          <ModalClose onClick={handleCloseModal} />
          <div className="flex flex-col gap-10 mb-20">
            <p>
              <span className="font-bold text-lg">Estate Name : </span>
              <span className="text-base font-medium">{estateDetails?.name}</span>
            </p>
            <p>
              <span className="font-bold text-lg">Address : </span>
              <span className="text-base font-medium"> {estateDetails?.address}</span>
            </p>
            <p>
              <span className="font-bold text-lg">State : </span>
              <span className="text-base font-medium">
                {estateDetails?.state || 'NIL'}
              </span>
            </p>
            <p>
              <span className="font-bold text-lg">Total Number of Staffs : </span>
              <span className="text-base font-medium">
                {estateDetails?.totalNumberOfStaff || 0}
              </span>
            </p>
            <p>
              <span className="font-bold text-lg">Total Number of Residents : </span>
              <span className="text-base font-medium">
                {estateDetails?.totalNumOfResidents || 0}
              </span>
            </p>
          </div>
          <div className="flex gap-5 items-center justify-center">
            <DefaultButton
              onClick={() => handleEditEstate(id)}
              color="black"
              type="submit"
              buttonType="secondary"
              className="w-24 text-center text-base font-medium bg-white text-black rounded-lg"
            >
              Edit
            </DefaultButton>
            <div className="flex">
              {isVerified && hasAccess ? (
                <DefaultButton
                  background="black"
                  color="white"
                  disabled
                  className="w-32 text-center text-base font-medium bg-black text-white rounded-lg"
                >
                  You're here
                </DefaultButton>
              ) : isVerified && isActive && accepted ? (
                <DefaultButton
                  background="black"
                  color="white"
                  onClick={() => handleGetAccess(id)}
                  className="w-32 text-center text-base font-medium bg-black text-white rounded-lg"
                >
                  Get Access
                </DefaultButton>
              ) : (
                isVerified && !accepted && (
                  <DefaultButton
                    background="black"
                    color="white"
                    onClick={() => isOwner ? handleGetAccess(id) : handleStaffInvite(id)}
                    className="w-32 text-center text-base font-medium bg-black text-white rounded-lg"
                  >
                    {isOwner ? 'Get Access' : 'Accept Invite'}
                  </DefaultButton>
                )
              )}
            </div>

          </div>
        </div>
      </Modal>
    </div>
  )
}

export default EstateCard